import { useEffect, useState } from "react";
import type { CSSObject } from "@emotion/react";
import {
  IonButton,
  IonContent,
  IonDatetime,
  IonDatetimeButton,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TimeZoneDisclaimer } from "components/Molecules/TimezoneDisclaimer/TimezoneDisclaimer";
import { usePanelFlow } from "components/PanelFlow";
import { PhoneInput } from "components/PhoneInput/PhoneInput";
import { isDateSelectable, isSameDate } from "components/RequestForm/hooks";
import { DateTime, Settings } from "luxon";
import { IntervalModel, IssueKeyType, IssueType } from "model";
import { useAuth } from "pages";
import { Alert, Asterisk, CheckboxInput, Colors, TimeInput } from "swing-components";
import { generateSwingSupportURL } from "swing-utils";

import { IssueParam, IssueParamListItem, reportIssue, translate } from "~utils";

import { ReportIssueKey, ReportIssueModalProps } from "../types";
import { getLatestReportableInterval } from "../utils";
import { getPriorReport, hasPriorReport, hasPriorReportType } from "./helpers";

/********* Styles ********/
const styles: Record<string, CSSObject> = {
  label: {
    fontWeight: 500,
  },
  // Button folds if this isn't set
  dtButton: {
    ":part(native)": {
      width: "max-content",
    },
  },
  submitButtonHolder: {
    padding: "16px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    marginTop: "auto",
  },
  listFill: {
    minHeight: "100%",
    background: Colors.white200,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  hideScroll: {
    "--offset-bottom": "auto !important",
    "--overflow": "auto",
    background: "var(--ion-toolbar-background, var(--ion-background-color, #000000)) !important",
    overflow: "auto",

    "&::-webkit-scrollbar": {
      width: 0,
      height: 0,
    },
  },
  labelWithDatePicker: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
};

/********* Types ********/
// "no-show" here is for the limbo state where
// neither confirm nor not confirmed has been selected.
type SelectionReasonStrings = IssueKeyType | "no-show";
type NoShowStrings = "confirmed" | "unconfirmed" | "unselected";

type ReportIssueFormProps = ReportIssueModalProps & {
  goToPanel: (panelKey: string) => void;
};

export function ReportIssueForm(props: ReportIssueFormProps) {
  const { state, onDismiss, extraProps, setState, goToPanel } = props;
  const { intervals, subName, status, requestId, school } = extraProps;
  const findInitialInterval = state?.initialIntervalId
    ? IntervalModel.getIntervalById(intervals, state.initialIntervalId)
    : undefined;
  // The logic for max allowed date is such that:
  //
  // * If the request is currently active, make the max selectable interval today.
  // * If the request is complete, make the max selectable interval the last day of the request.
  // * If an initial interval is set (arrived on this page via a specific report issue edit button),
  //   that date is set.
  const maxAllowedDate = findInitialInterval || getLatestReportableInterval(intervals);

  /********* Hooks ********/
  const { userInfo } = useAuth();
  const queryClient = useQueryClient();

  const [hasApiError, setHasApiError] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState<IntervalModel>(maxAllowedDate);

  // The initial setting of these two values happen in the useEffect call.
  // An initial value has to be passed to useState because Typescript is
  // lovely like that.

  const [arrivedLateTime, setArrivedLateTime] = useState<DateTime>(selectedInterval.start);
  const [leftEarlyTime, setLeftEarlyTime] = useState<DateTime>(selectedInterval.end);
  const [selectedReasons, setSelectedReasons] = useState<SelectionReasonStrings[]>([]);
  const [noShowConfirmed, setNoShowConfirmed] = useState<NoShowStrings>("unselected");
  const [phone, setPhone] = useState<string>("");
  const [details, setDetails] = useState<string | undefined>("");

  useEffect(() => {
    Settings.defaultZone = school.tzString;
    if (hasPriorReport(selectedInterval)) {
      // Details

      if (selectedInterval.comments) {
        setDetails(selectedInterval.comments);
      }

      const tempSelectedReasons: SelectionReasonStrings[] = [];

      // Sub Late
      if (hasPriorReportType(selectedInterval, IssueType.Late)) {
        const priorLateIssue = getPriorReport(selectedInterval, IssueType.Late);
        setArrivedLateTime(priorLateIssue.tardyTimestamp);
        tempSelectedReasons.push(IssueType.Late);
      }

      // Sub Lunch Late
      if (hasPriorReportType(selectedInterval, IssueType.LunchLate)) {
        tempSelectedReasons.push(IssueType.LunchLate);
      }

      // Sub Left Early
      if (hasPriorReportType(selectedInterval, IssueType.SubLeftEarly)) {
        const priorLeftEarlyIssue = getPriorReport(selectedInterval, IssueType.SubLeftEarly);
        setLeftEarlyTime(priorLeftEarlyIssue.tardyTimestamp);
        tempSelectedReasons.push(IssueType.SubLeftEarly);
      }

      // Sub no show (confirmed)
      if (hasPriorReportType(selectedInterval, IssueType.NoShowConfirmed)) {
        tempSelectedReasons.push("no-show");
        setNoShowConfirmed("confirmed");
      }

      // Sub no show (unconfirmed)
      if (hasPriorReportType(selectedInterval, IssueType.NoShowUnConfirmed)) {
        tempSelectedReasons.push("no-show");
        setNoShowConfirmed("unconfirmed");
      }

      // Sub urgent on campus concern
      if (hasPriorReportType(selectedInterval, IssueType.UrgentConcern)) {
        tempSelectedReasons.push(IssueType.UrgentConcern);
      }

      setSelectedReasons(tempSelectedReasons);
    } else {
      setSelectedReasons([]);
      setArrivedLateTime(selectedInterval.start);
      setLeftEarlyTime(selectedInterval.end);
    }
  }, [selectedInterval, school.tzString]);

  /********* Mutation ********/
  const { mutate: submitReportIssue } = useMutation({
    mutationFn: (submissionData: IssueParam) => {
      return reportIssue(requestId, submissionData);
    },
    onSuccess: () => {
      if (selectedReasons.includes("no-show")) {
        if (noShowConfirmed === "confirmed") {
          setState({ reportedIssues: [IssueType.NoShowConfirmed] });
        } else {
          setState({ reportedIssues: [IssueType.NoShowUnConfirmed] });
        }
      } else {
        setState({ reportedIssues: selectedReasons });
      }
      goToPanel(ReportIssueKey.ReportIssueConfirmPanel);
      queryClient.invalidateQueries();
    },
    onError: () => {
      setHasApiError(true);
    },
  });

  const findInterval = (dateString: string) => {
    return intervals.find((item) => {
      const originalStart = item.start;
      const selectedStart = DateTime.fromISO(dateString);
      return isSameDate(originalStart, selectedStart);
    });
  };

  const isEditingToday = isSameDate(selectedInterval.start, DateTime.now());

  const showSwingContactFields =
    isEditingToday && status !== "CANCELLED" && noShowConfirmed === "unconfirmed";

  const isButtonDisabled = () => {
    if (selectedReasons.includes("no-show")) {
      if (noShowConfirmed === "unselected") {
        return true;
      }
    }

    if (selectedInterval.issues.length >= 1) {
      // Allow issue to be deleted.
      return false;
    }

    if (selectedReasons.length >= 1) {
      return false;
    }

    return true;
  };

  function changeSelectedReasons(selection: SelectionReasonStrings) {
    if (selectedReasons.includes(selection)) {
      setSelectedReasons((old) => {
        const filteredArr = old.filter((item) => item !== selection);
        return filteredArr;
      });
      return;
    }
    if (selection === "no-show") {
      setSelectedReasons(["no-show"]);
      return;
    }

    setSelectedReasons((old) => {
      // If "no-show" was previously selected, and admin picks
      // a new selection, replace it w/ a new reason, but also
      // reset state
      if (old.includes("no-show")) {
        setNoShowConfirmed("unselected");
        setDetails("");
        setPhone("");
        return [selection];
      }
      return [...old, selection];
    });
  }

  function onReportSubmit() {
    const issueList = selectedReasons.map((item): IssueParamListItem => {
      switch (item) {
        case IssueType.Late:
          return {
            issue: IssueType.Late,
            "tardy-timestamp": arrivedLateTime.toISO(),
            "tardy-offset": Math.abs(arrivedLateTime.offset),
          };
        case IssueType.LunchLate:
          return {
            issue: IssueType.LunchLate,
          };
        case IssueType.SubLeftEarly:
          return {
            issue: IssueType.SubLeftEarly,
            "tardy-timestamp": leftEarlyTime.toISO(),
            "tardy-offset": Math.abs(leftEarlyTime.offset),
          };
        case "no-show":
          if (noShowConfirmed === "confirmed") {
            return {
              issue: IssueType.NoShowConfirmed,
            };
          } else {
            return {
              issue: IssueType.NoShowUnConfirmed,
            };
          }
        case IssueType.UrgentConcern:
          return {
            issue: IssueType.UrgentConcern,
          };
        default:
          return {
            issue: null,
          };
      }
    });

    const requestPayload: IssueParam = {
      "interval-id": selectedInterval.eid,
      contact: phone,
      comments: details,
      issues: issueList,
    };

    submitReportIssue(requestPayload);
  }

  /********* Render ********/
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{translate("REPORT_ISSUE_FORM_HEADER")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      {hasApiError && (
        <div className="ion-margin-top">
          <Alert
            color="danger"
            onDismiss={() => setHasApiError(false)}
            showIcon
            title={translate("LABEL_SOMETHING_WENT_WRONG")}
            className="mx-4"
          >
            <span>
              {translate("LABEL_CONTACT_SUPPORT_1")}{" "}
              <a
                target="_blank"
                rel="noreferrer"
                href={generateSwingSupportURL("/hc/en-us/requests/new")}
              >
                {translate("LABEL_CONTACT_SUPPORT")}
              </a>{" "}
              {translate("LABEL_CONTACT_SUPPORT_2")}
            </span>
          </Alert>
        </div>
      )}
      <IonContent css={hasApiError ? styles.hideScroll : {}}>
        <IonList css={styles.listFill} className="ion-padding">
          <div>
            <IonItem lines="none">{translate("REPORT_ISSUE_FORM_SUBHEADER", subName)}</IonItem>
            {userInfo?.isSuperAdmin && (
              <TimeZoneDisclaimer
                hideHeader
                isSuperAdmin={userInfo?.isSuperAdmin}
                schoolName={school.name}
                timeZone={school.tzString}
              />
            )}
            <IonItem disabled={intervals.length <= 1} lines="inset">
              <IonLabel className="font-medium">
                {translate("REPORT_ISSUE_FORM_SELECT_DAY")}
              </IonLabel>
              <IonDatetimeButton datetime="issueDay" />
              <IonModal keepContentsMounted className="stackedModal">
                <IonDatetime
                  className="lowercase"
                  showDefaultButtons
                  id="issueDay"
                  presentation="date"
                  min={intervals[0].start.toString()}
                  max={maxAllowedDate.start.toString()}
                  value={selectedInterval.start.toISO()}
                  isDateEnabled={isDateSelectable}
                  onIonChange={(newDate: CustomEvent) => {
                    const foundVal = findInterval(newDate.detail.value);
                    if (foundVal) {
                      setSelectedInterval(foundVal);
                    }
                  }}
                />
              </IonModal>
            </IonItem>
            <IonItem lines="none" css={styles.label}>
              <IonLabel>{translate("REPORT_ISSUE_FORM_SELECT_REASON")}</IonLabel>
            </IonItem>
            <CheckboxInput
              label={
                <div css={styles.labelWithDatePicker}>
                  {translate("REPORT_ISSUE_FORM_LATE")}
                  <TimeInput
                    min={selectedInterval.start}
                    value={arrivedLateTime}
                    isDisabled={!selectedReasons.includes(IssueType.Late)}
                    onChange={(newDate) => setArrivedLateTime(newDate)}
                    showTextInputOnDesktop
                  />
                </div>
              }
              isChecked={selectedReasons.includes(IssueType.Late)}
              onChange={() => changeSelectedReasons(IssueType.Late)}
              hasBorderBottom
            />
            <CheckboxInput
              label={translate("REPORT_ISSUE_FORM_LATE_LUNCH")}
              isChecked={selectedReasons.includes(IssueType.LunchLate)}
              onChange={() => changeSelectedReasons(IssueType.LunchLate)}
              hasBorderBottom
            />
            <CheckboxInput
              label={
                <div css={styles.labelWithDatePicker}>
                  {translate("REPORT_ISSUE_FORM_LEFT_EARLY")}
                  <TimeInput
                    max={selectedInterval.end}
                    value={leftEarlyTime}
                    isDisabled={!selectedReasons.includes(IssueType.SubLeftEarly)}
                    onChange={(newDate) => setLeftEarlyTime(newDate)}
                    showTextInputOnDesktop
                  />
                </div>
              }
              isChecked={selectedReasons.includes(IssueType.SubLeftEarly)}
              onChange={() => changeSelectedReasons(IssueType.SubLeftEarly)}
              hasBorderBottom
            />
            <CheckboxInput
              label={translate("REPORT_ISSUE_FORM_NO_SHOW")}
              isChecked={selectedReasons.includes("no-show")}
              onChange={() => changeSelectedReasons("no-show")}
              hasBorderBottom={!selectedReasons.includes("no-show")}
            />
            {selectedReasons.includes("no-show") && (
              <div css={{ padding: "0 12px" }}>
                <IonItem>
                  <IonLabel css={styles.label}>
                    {`${translate("REPORT_ISSUE_FORM_NO_SHOW_CONFIRM_LABEL")} `}
                    <Asterisk />
                  </IonLabel>
                </IonItem>
                <IonRadioGroup
                  onIonChange={(e) => setNoShowConfirmed(e.target.value)}
                  value={noShowConfirmed}
                >
                  <IonItem>
                    <IonRadio labelPlacement="end" justify="start" value="confirmed">
                      {translate("LABEL_YES")}
                    </IonRadio>
                  </IonItem>
                  <IonItem>
                    <IonRadio labelPlacement="end" justify="start" value="unconfirmed">
                      {translate("LABEL_NO")}
                    </IonRadio>
                  </IonItem>
                </IonRadioGroup>
                {showSwingContactFields && (
                  <>
                    <IonItem lines="none">
                      <IonLabel css={styles.label}>
                        {translate("REPORT_ISSUE_FORM_DETAILS_PHONE_LABEL")}
                      </IonLabel>
                    </IonItem>
                    <IonItem>
                      <PhoneInput
                        value={phone}
                        onIonInput={setPhone}
                        aria-label={translate("REPORT_ISSUE_FORM_DETAILS_PHONE_LABEL")}
                      />
                    </IonItem>
                  </>
                )}
                <IonItem lines="none">
                  <IonLabel css={styles.label}>
                    {translate("REPORT_ISSUE_FORM_DETAILS_LABEL")}
                  </IonLabel>
                </IonItem>
                <IonItem>
                  <IonInput
                    aria-label={translate("REPORT_ISSUE_FORM_DETAILS_LABEL")}
                    placeholder={translate("REPORT_ISSUE_FORM_DETAILS_PLACEHOLDER")}
                    labelPlacement="stacked"
                    value={details}
                    onIonInput={(e) => setDetails(e.target.value?.toString())}
                  />
                </IonItem>
              </div>
            )}
            {/* Urgent Concern Option */}
            {/* Do NOT show if past request */}
            {/* See: https://linear.app/swing-education/issue/MKTP-962/%5Bfe%5D-remove-urgent-concern-option-from-past-requests */}
            {status !== "COMPLETED" && (
              <CheckboxInput
                label={translate("REPORT_ISSUE_FORM_URGENT_CONCERN")}
                isChecked={selectedReasons.includes(IssueType.UrgentConcern)}
                onChange={() => changeSelectedReasons(IssueType.UrgentConcern)}
                hasBorderBottom
              />
            )}
          </div>

          <div css={styles.submitButtonHolder}>
            <IonButton
              onClick={() => onReportSubmit()}
              expand="block"
              disabled={isButtonDisabled()}
            >
              {translate("LABEL_SUBMIT")}
            </IonButton>

            <IonButton onClick={() => onDismiss()} expand="block" fill="outline">
              {translate("LABEL_CANCEL")}
            </IonButton>
          </div>
        </IonList>
      </IonContent>
    </IonPage>
  );
}

export function ReportIssueFormPanel() {
  const panelFlowProps = usePanelFlow();
  const { extraProps, goToPanel, state, setState } = panelFlowProps;

  return (
    <ReportIssueForm
      state={state}
      extraProps={panelFlowProps.extraProps}
      onDismiss={extraProps.onDismiss}
      goToPanel={goToPanel}
      setState={setState}
    />
  );
}
